import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Send, Loader2 } from 'lucide-react';
import api from '../utils/api';

const ContactContent = () => {
    // Form State
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: ''
    });

    // Settings State - Inizializzato vuoto per gestire la logica condizionale
    const [settings, setSettings] = useState({
        contact_phone: '',
        contact_email: ''
    });

    const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

    // Fetch dynamic contact info
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await api.get('/content/settings');
                if (response.data.success && response.data.data) {
                    setSettings(prev => ({ ...prev, ...response.data.data }));
                }
            } catch (error) {
                console.error("Unable to load contact settings", error);
            }
        };
        fetchSettings();
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('sending');

        try {
            const res = await api.post('/content/contact', formData);
            if (res.data.success) {
                setStatus('success');
                setFormData({ name: '', email: '', message: '' });
                setTimeout(() => setStatus('idle'), 5000);
            } else {
                setStatus('error');
            }
        } catch (error) {
            console.error("Error sending message", error);
            setStatus('error');
        }
    };

    return (
        <div className="pt-36 px-6 md:px-8 max-w-7xl mx-auto pb-24 animate-fade-in">

            {/* --- HEADER --- */}
            <div className="text-center mb-20">
                <span className="text-brand font-bold text-sm tracking-widest uppercase mb-2 block">Contact Us</span>
                <h1 className="text-5xl font-serif font-bold text-primary mb-6">Get in Touch</h1>
                <p className="text-xl text-gray-500 max-w-2xl mx-auto">
                    We are here to help you plan your perfect stay. Reach out to us for any inquiries.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">

                {/* --- LEFT COL: CONTACT INFO --- */}
                <div className="space-y-10">
                    <h2 className="text-3xl font-serif font-bold text-gray-900 mb-8">Contact Information</h2>

                    {/* Phone - Mostrato solo se contact_phone esiste */}
                    {settings.contact_phone && (
                        <div className="flex items-start gap-6 group">
                            <div className="bg-gray-50 p-5 rounded-2xl text-brand group-hover:bg-brand group-hover:text-white transition-colors duration-300">
                                <Phone className="w-7 h-7" />
                            </div>
                            <div>
                                <h3 className="font-bold text-lg text-primary mb-1">Phone</h3>
                                <p className="text-gray-600 text-lg">{settings.contact_phone}</p>
                                <p className="text-sm text-gray-400 mt-1">Available for your calls</p>
                            </div>
                        </div>
                    )}

                    {/* Email - Mostrato solo se contact_email esiste */}
                    {settings.contact_email && (
                        <div className="flex items-start gap-6 group">
                            <div className="bg-gray-50 p-5 rounded-2xl text-brand group-hover:bg-brand group-hover:text-white transition-colors duration-300">
                                <Mail className="w-7 h-7" />
                            </div>
                            <div>
                                <h3 className="font-bold text-lg text-primary mb-1">Email</h3>
                                <p className="text-gray-600 text-lg">{settings.contact_email}</p>
                                <p className="text-sm text-gray-400 mt-1">We usually reply within 24 hours</p>
                            </div>
                        </div>
                    )}

                    {/* Office
                    <div className="flex items-start gap-6 group">
                        <div className="bg-gray-50 p-5 rounded-2xl text-brand group-hover:bg-brand group-hover:text-white transition-colors duration-300">
                            <MapPin className="w-7 h-7" />
                        </div>
                        <div>
                            <h3 className="font-bold text-lg text-primary mb-1">Main Office</h3>
                            <p className="text-gray-600 text-lg">Via della Spiga, 10</p>
                            <p className="text-gray-600 text-lg">20121 Milano, Italy</p>
                        </div>
                    </div>*/}
                </div>

                {/* --- RIGHT COL: FORM --- */}
                <div className="bg-white rounded-[2.5rem] shadow-2xl p-8 md:p-12 border border-gray-100 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-brand/5 rounded-bl-full -z-0"></div>

                    <h3 className="text-2xl font-bold text-primary mb-8 relative z-10">Send us a message</h3>

                    <form className="space-y-6 relative z-10" onSubmit={handleSubmit}>
                        {/* ... (resto del form invariato) ... */}
                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase text-gray-500 ml-1">Name</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Your full name"
                                required
                                className="w-full p-4 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all placeholder:text-gray-400"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase text-gray-500 ml-1">Email</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="name@example.com"
                                required
                                className="w-full p-4 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all placeholder:text-gray-400"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase text-gray-500 ml-1">Message</label>
                            <textarea
                                rows={4}
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                placeholder="How can we help you?"
                                required
                                className="w-full p-4 bg-gray-50 rounded-xl border border-gray-200 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all placeholder:text-gray-400 resize-none"
                            ></textarea>
                        </div>

                        <button
                            disabled={status === 'sending'}
                            className={`w-full text-white py-4 rounded-xl font-bold transition-all shadow-lg flex items-center justify-center gap-2
                                ${status === 'sending' ? 'bg-gray-400 cursor-not-allowed' : 'bg-brand hover:bg-[#c42e40] hover:shadow-xl transform hover:-translate-y-0.5'}`}
                        >
                            {status === 'sending' ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" /> Sending...
                                </>
                            ) : (
                                <>
                                    Send Message <Send className="w-4 h-4" />
                                </>
                            )}
                        </button>

                        {status === 'success' && (
                            <div className="text-green-700 font-medium text-center bg-green-50 p-4 rounded-xl border border-green-100 animate-fade-in">
                                Message sent successfully! We will get back to you soon.
                            </div>
                        )}
                        {status === 'error' && (
                            <div className="text-red-700 font-medium text-center bg-red-50 p-4 rounded-xl border border-red-100 animate-fade-in">
                                Something went wrong. Please try again later.
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ContactContent;